import { ComputeStorage as ComputeStorageSchema } from '@schema';
import {
  EXTERNAL_STORAGE_STATUS_ICON_MAP,
  EXTERNAL_STORAGE_STATUS_TEXT,
  FC_DRIVERS, ISCSI_DRIVERS,
  LEGACY_MANAGEMENT_ADDRESS_MAPPING,
  NFS_VERSIONS,
  NFS_VERSIONS_LABELS,
  NON_STANDARD_DRIVER_PROTOCOL_FIELD_MAPPING,
  NON_STANDARD_MANAGEMENT_ADDRESS_MAPPING,
  PROTOCOL_LABELS,
  PROTOCOLS,
  REVERT_TO_SNAPSHOT_SUPPORTED_DRIVERS,
  TRANSPORT_TYPE,
  TRANSPORT_TYPE_LABELS,
  VOLUME_DRIVER_LABELS,
  VOLUME_DRIVERS
} from '@/common/constants/externalStorages.constants';
import { BYTES_IN_GIBIBYTES } from '@/common/utils/formatter.number';
import { NFS_VERSION_MOUNT_OPTIONS_REGEXP } from '@/common/utils/validate';
import useComputeStoragePoliciesStore from '@/common/store/pinia/computeStoragePolicies';

interface DriverConfig {
  volume_driver: string;
  storage_protocol?: 'fc' | 'iscsi' | 'nfs';
}

interface DriverInfo {
  protocol: 'fc' | 'iscsi' | 'nfs' | null;
  driverName: string | null;
}

export function determineDriverInfo(volumeDriver: string, externalStorage?: ComputeStorageSchema): DriverInfo {
  const result: DriverInfo = {
    protocol: null,
    driverName: null
  };

  const rawStorageProtocol = externalStorage?.params?.storage_protocol || externalStorage?.params?.[NON_STANDARD_DRIVER_PROTOCOL_FIELD_MAPPING[volumeDriver]];
  // Some drivers (e.g. IBM Storage FC) store 'fibre_channel' in their protocol field,
  // but our internal constant is PROTOCOLS.FIBRE_CHANNEL = 'fc'. Normalize here so all
  // comparisons below work correctly regardless of which value the backend returns.
  const storageProtocol = rawStorageProtocol === 'fibre_channel' ? PROTOCOLS.FIBRE_CHANNEL : rawStorageProtocol;
  const volumeDriverEntries = Object.entries(VOLUME_DRIVERS);
  const volumeDriverMatch = volumeDriverEntries.find(([_, value]) => value === volumeDriver);

  if (volumeDriverMatch) {
    result.driverName = VOLUME_DRIVER_LABELS[volumeDriverMatch[1]];

    if (volumeDriver === VOLUME_DRIVERS.PURE_FC) {
      result.protocol = 'fc';
    } else if (volumeDriver === VOLUME_DRIVERS.PURE_ISCSI) {
      result.protocol = 'iscsi';
    } else if (volumeDriver === VOLUME_DRIVERS.NFS) {
      result.protocol = 'nfs';
    } else if (volumeDriver === VOLUME_DRIVERS.NETAPP && externalStorage?.params) {
      const protocolValue = externalStorage.params.netapp_storage_protocol as string | undefined;
      if (protocolValue === 'fc' || protocolValue === 'iscsi' || protocolValue === 'nfs') {
        result.protocol = protocolValue;
      }
    }

    return result;
  }

  if (!storageProtocol || storageProtocol === PROTOCOLS.FIBRE_CHANNEL) {
    const fcDriverEntry = Object.entries(FC_DRIVERS).find(
      ([_, config]) => (config as DriverConfig).volume_driver === volumeDriver
    );

    if (fcDriverEntry) {
      const [driverName, config] = fcDriverEntry;
      result.driverName = driverName;
      result.protocol = (config as DriverConfig).storage_protocol || 'fc';
      return result;
    }
  }

  if (!storageProtocol || storageProtocol === PROTOCOLS.ISCSI) {
    const iscsiDriverEntry = Object.entries(ISCSI_DRIVERS).find(
      ([_, config]) => (config as DriverConfig).volume_driver === volumeDriver
    );

    if (iscsiDriverEntry) {
      const [driverName, config] = iscsiDriverEntry;
      result.driverName = driverName;
      result.protocol = (config as DriverConfig).storage_protocol || 'iscsi';
      return result;
    }
  }

  return result;
}

function buildVolumeDriverFieldMap(mapping: Record<string, string | null>): Map<string, string | null> {
  const map = new Map<string, string | null>();

  Object.entries(mapping).forEach(([driverName, fieldName]) => {
    if (driverName in ISCSI_DRIVERS) {
      map.set(ISCSI_DRIVERS[driverName]?.volume_driver, fieldName);
    } else if (driverName in FC_DRIVERS) {
      map.set(FC_DRIVERS[driverName]?.volume_driver, fieldName);
    }
  });

  return map;
}

const volumeDriverToManagementFieldMap = buildVolumeDriverFieldMap(NON_STANDARD_MANAGEMENT_ADDRESS_MAPPING);
const volumeDriverToLegacyManagementFieldMap = buildVolumeDriverFieldMap(LEGACY_MANAGEMENT_ADDRESS_MAPPING);

export function getDriverStaticProperties(volumeDriver: string, protocol?: string): Record<string, unknown> {
  if (protocol !== PROTOCOLS.FIBRE_CHANNEL && protocol !== PROTOCOLS.ISCSI) return {};
  const driverList = protocol === PROTOCOLS.FIBRE_CHANNEL ? FC_DRIVERS : ISCSI_DRIVERS;
  const entry = Object.values(driverList).find(d => d.volume_driver === volumeDriver);
  if (!entry) return {};
  const { volume_driver: _, ...staticProps } = entry as Record<string, unknown>;
  return staticProps;
}

export function getManagementAddressField(volumeDriver: string, protocol?: string): string | null {
  if (volumeDriver === VOLUME_DRIVERS.NFS || protocol === 'nfs') {
    return 'nas_host';
  }

  if (volumeDriver === VOLUME_DRIVERS.NETAPP) {
    return 'netapp_server_hostname';
  }

  if (volumeDriver && volumeDriverToManagementFieldMap.has(volumeDriver)) {
    return volumeDriverToManagementFieldMap.get(volumeDriver) || null;
  }

  return 'san_ip';
}

export function getLegacyManagementAddressField(volumeDriver: string): string | null {
  return volumeDriverToLegacyManagementFieldMap.get(volumeDriver) || null;
}

export function resolveManagementAddress(params: Record<string, any> = {}): string {
  const volumeDriver = params.volume_driver || '';
  const fieldName = getManagementAddressField(volumeDriver);

  // If fieldName is null, it means this driver doesn't use a management address
  if (fieldName === null) {
    return '';
  }

  if (params[fieldName]) {
    return params[fieldName];
  }

  // Drivers that used to store the management address under a different param
  // (Huawei: metro_san_address) keep reading the old value for existing deployments.
  const legacyFieldName = getLegacyManagementAddressField(volumeDriver);

  return (legacyFieldName && params[legacyFieldName]) || '';
}

const mapper = (externalStorage: ComputeStorageSchema) => {
  const computeStoragePoliciesStore = useComputeStoragePoliciesStore();
  const volumeDriver = externalStorage.params?.volume_driver as string;

  const driverInfo = determineDriverInfo(volumeDriver, externalStorage);
  let { protocol } = driverInfo;
  const { driverName } = driverInfo;

  if (!protocol && volumeDriver !== VOLUME_DRIVERS.NETAPP) {
    const protocolValue = externalStorage.params?.storage_protocol as string | undefined;
    if (protocolValue === 'fc' || protocolValue === 'iscsi' || protocolValue === 'nfs') {
      protocol = protocolValue;
    }
  }

  let nfsVersion = '';
  let transportType = '';

  const isNetApp = volumeDriver === VOLUME_DRIVERS.NETAPP;
  const managementAddress = resolveManagementAddress(externalStorage?.params);

  if (volumeDriver === VOLUME_DRIVERS.NFS && externalStorage.params?.nfs_mount_options) {
    const mountOptions = (externalStorage.params.nfs_mount_options || '') as string;
    const versionMatch = NFS_VERSION_MOUNT_OPTIONS_REGEXP.exec(mountOptions);
    nfsVersion = versionMatch?.[0] ?? NFS_VERSIONS.NFS_4_1;
  }

  if (isNetApp && externalStorage.params?.netapp_transport_type) {
    transportType = (externalStorage.params.netapp_transport_type || '') as string;
  } else {
    transportType = externalStorage?.params?.driver_ssl_cert_verify ? TRANSPORT_TYPE.HTTPS : TRANSPORT_TYPE.HTTP;
  }

  const relatedPolicies = computeStoragePoliciesStore.getItemsByProp('storage')(
    typeof externalStorage?.params?.volume_backend_name === 'string'
      ? externalStorage?.params?.volume_backend_name
      : undefined
  );
  const statusIcon = EXTERNAL_STORAGE_STATUS_ICON_MAP[externalStorage.status || ''];
  const statusText = EXTERNAL_STORAGE_STATUS_TEXT[externalStorage.status || ''];

  let multipathValue: boolean | null = null;
  if (volumeDriver !== VOLUME_DRIVERS.NFS) {
    const useMultipathParam = externalStorage?.params?.use_multipath_for_image_xfer;
    multipathValue = typeof useMultipathParam === 'boolean' ? useMultipathParam : true;
  }

  return {
    ...externalStorage,
    usage: {
      used: Number(externalStorage.statistics?.used_capacity ?? 0) * BYTES_IN_GIBIBYTES,
      size: Number(externalStorage.statistics?.total_capacity ?? 0) * BYTES_IN_GIBIBYTES,
    },
    managementAddress,
    protocolValue: protocol,
    protocol: protocol ? PROTOCOL_LABELS[protocol] : null,
    volumeDriverValue: volumeDriver,
    volumeDriver: driverName,
    canRevertToSnapshot: REVERT_TO_SNAPSHOT_SUPPORTED_DRIVERS.includes(volumeDriver),
    volumesCount: externalStorage?.statistics?.volume_count ?? 0,
    version: nfsVersion ? NFS_VERSIONS_LABELS[nfsVersion] : null,
    nfsSharePath: externalStorage?.params?.nas_share_path,
    imageVolumeCache: externalStorage?.params?.image_volume_cache_enabled,
    multipath: multipathValue,
    svmName: externalStorage?.params?.netapp_vserver,
    enabled: externalStorage?.enabled,
    transportTypeValue: transportType,
    transportType: transportType ? TRANSPORT_TYPE_LABELS[transportType] : null,
    chapAuthentication: externalStorage?.params?.use_chap_auth,
    volumeDriverSecretParamsCount: externalStorage?.secret_params ? Object.keys(externalStorage.secret_params).length : 0,
    volumeBackend: externalStorage?.params?.volume_backend_name,
    relatedPolicies,
    statusIcon,
    statusText
  };
};

export type ExternalStorage = ReturnType<typeof mapper>;

export default mapper;
